import { useEffect, useState, useRef } from "react";
import { api, getUser } from "@/lib/api";
import { Bell, Check } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

const KIND_META = {
  welcome: { color: "text-[#D4AF37]", label: "Welcome" },
  booking_pending: { color: "text-yellow-400", label: "Booking" },
  booking_reserved: { color: "text-cyan-400", label: "Reserved" },
  booking_paid: { color: "text-emerald-400", label: "Paid" },
  booking_assigned: { color: "text-blue-400", label: "Assigned" },
  booking_completed: { color: "text-[#D4AF37]", label: "Completed" },
  job_completed: { color: "text-[#D4AF37]", label: "Payout" },
};

const timeAgo = (iso) => {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
};

export const NotificationsBell = () => {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const load = async () => {
    if (!getUser()) return;
    try {
      const res = await api.get("/notifications");
      setItems(res.data.items);
      setUnread(res.data.unread);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => { clearInterval(t); document.removeEventListener("mousedown", onClick); };
  }, []);

  const markAll = async () => {
    await api.post("/notifications/read-all");
    load();
  };

  if (!getUser()) return null;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => { setOpen(o => !o); if (!open) load(); }}
        data-testid="notifications-bell"
        aria-label="Notifications"
        className="relative w-9 h-9 rounded-full border border-white/10 flex items-center justify-center text-zinc-300 hover:text-[#D4AF37] hover:border-[#D4AF37]/40 transition"
      >
        <Bell weight="duotone" size={18} />
        {unread > 0 && (
          <span
            data-testid="notifications-unread-count"
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#D4AF37] text-black text-[10px] font-bold flex items-center justify-center"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div
          data-testid="notifications-panel"
          className="absolute right-0 top-12 w-[380px] max-h-[520px] rounded-2xl border border-white/10 bg-[#121214] shadow-2xl overflow-hidden z-50"
        >
          <div className="flex items-center justify-between p-4 border-b border-white/10">
            <div className="font-serif-display text-lg text-white">Notifications</div>
            {unread > 0 && (
              <button
                onClick={markAll}
                data-testid="mark-all-read-btn"
                className="text-xs text-[#D4AF37] hover:text-[#F3E5AB] flex items-center gap-1"
              >
                <Check size={12} weight="bold" /> Mark all read
              </button>
            )}
          </div>
          <div className="overflow-y-auto max-h-[440px]">
            {items.length === 0 ? (
              <div className="p-8 text-center text-sm text-zinc-500">You're all caught up.</div>
            ) : (
              items.map(n => {
                const km = KIND_META[n.kind] || { color: "text-zinc-400", label: "Update" };
                return (
                  <div
                    key={n.id}
                    data-testid={`notif-item-${n.kind}`}
                    className={`p-4 border-b border-white/5 transition ${n.read ? "bg-transparent" : "bg-[#D4AF37]/5"}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className={`text-[10px] uppercase tracking-[0.25em] font-semibold ${km.color}`}>{km.label}</div>
                      <div className="text-[10px] text-zinc-500">{timeAgo(n.created_at)}</div>
                    </div>
                    <div className="text-sm text-white mt-1 font-medium">{n.title}</div>
                    <div className="text-xs text-zinc-400 mt-1 leading-relaxed">{n.message}</div>
                  </div>
                );
              })
            )}
          </div>
          <div className="p-3 border-t border-white/10 text-center">
            <Link to="/dashboard" className="text-xs text-zinc-400 hover:text-[#D4AF37]" onClick={() => setOpen(false)}>
              Go to dashboard →
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationsBell;
